import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ViewEncapsulation,
  OnDestroy,
  inject,
  signal,
  Signal,
  computed,
} from '@angular/core';
import { ActivatedRoute, Data, NavigationStart, Router } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSort } from '@angular/material/sort';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { BehaviorSubject, filter, iif, noop, Observable, of, Subject, switchMap } from 'rxjs';

import {
  Dictionary,
  FilterListItemChangeArgs,
  FilterListItemModel,
  FilterListItemValueModel,
  RedirectItem,
  TsOverlayComponent,
  TsPaginatorChangeEvent,
  TsPaginatorComponent,
  TsSnackbarService,
  TsTableService,
} from 'tsui';
import cloneDeep from 'lodash-es/cloneDeep';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Actions, ofActionCompleted, ofActionDispatched, Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { AccountActions } from '@state/account/actions';
import { FieldDefsState } from '@state/fields/state';
import { AccountService } from 'app/core';
import { Account, FieldDef, SavedView, User, QueryConfig } from '@core/models';
import { LayoutService } from 'app/layout/layout.service';
import { UserService } from 'app/layout/common/user/user.service';
import { CompaniesService } from '@modules/companies/companies.service';
import { Company, CompanyPagination, CompanyStandardFieldsMap } from '@modules/companies/companies.types';
import UrlUtils from '@shared/Utils/url.utils';
import { Project } from '@modules/projects/projects.types';
import { FileFormat, RelatedToType } from '@shared/enums';
import { generateDownloadableFile, removeArchivedFieldsFromTableDefinition } from '@shared/functions';
import { MassUpdateTagsDialog } from '@shared/modals/mass-update-tags/mass-update-tags.component';
import { CustomFormOptions, CustomField } from '@shared/modules/custom-form/custom-form.types';
import { CustomFormComponent } from '@shared/modules/custom-form/custom-form/custom-form.component';
import { CompanyActions } from '../state/actions';
import { GraphqlConnection, GraphqlParams, GraphqlResultPageInfo } from '../../../shared/models/graphql';
import { CompanyState } from '../state/state';
import { FeatureFlag, NewPermission, Permission } from '../../../core/enums';
import { ObjectsManagerState } from '../../settings/objects-manager/state/state';
import { LookupService } from '@services/lookup.service';
import { AddEngagementDialogComponent } from '@modules/engagements/modals/add-engagement/add-engagement.component';
import { ChangeType, OriginalStates, TableSortState } from '@shared/models';
import { TsFilterUtilsService } from '@services/ts-filter-utils.service';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';
import { SavedViewsSidebarService } from '@shared/services/saved-views-sidebar.service';
import { FormControl } from '@angular/forms';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { ProjectsService } from '@modules/projects/projects.service';
import { SortChangedEvent } from 'ag-grid-enterprise';
import { UserState } from '@state/user/state';
import { TsTableTagsEditor } from '@shared/components/ts-table-renderers/editors/table-tags-editor.component';
import { nonEditableStyle } from 'tsui/lib/cross-cutting/util';
import { SavedViewUtilsService } from '@services/saved-view-utils.service';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';

@Component({
  selector: 'companies-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss', '../../../shared/styles/ts-page-header-list.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompaniesListComponent extends OnDestroyMixin implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) private _sort: MatSort;
  @ViewChild(TsPaginatorComponent) private _paginator: TsPaginatorComponent;
  @ViewChild('filtersOverlayBox') private _filtersOverlayBox: TsOverlayComponent;

  private _project: Project;
  private _pageSize: number = 20;
  private readonly _companiesCount = new BehaviorSubject(0);
  private readonly _defaultColumns = ['id', 'name', 'created_at', 'tag_list'];
  private readonly _fieldsToRemove = ['city', 'created_at', 'id', 'postal_code', 'state', 'updated_at'];
  private _changeTriggers: Set<string> = new Set();
  private _originalStates: OriginalStates;
  protected readonly Permission = Permission;

  sortState: TableSortState = {
    active: 'name',
    direction: 'asc',
  };
  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  routeData: Data;
  pagination: CompanyPagination;
  queryFilters: Partial<QueryConfig.QueryFilters>;
  exportingCsv: boolean = false;
  query: string = '';
  account: Account;
  user: User;
  companiesData: Company[];
  fieldDefs: FieldDef[] = [];
  filteredFieldDefs: FieldDef[] = [];
  companiesTableColumns: string[];
  displayColumns: string[];
  selection = new SelectionModel<number>(true, []);
  readonly relatedToTypeCompany = RelatedToType.Company;
  savedViewsSidebarConfig: TsPageSidenavConfig;
  protected readonly NewPermission = NewPermission;
  readonly savedView = signal<SavedView>(undefined);
  readonly isLoading = signal(false);

  columnDefs: ColumnDef[] = [];
  tsActions: TableActions[] = [];
  relatedToRedirectConfig: Dictionary<RedirectItem> = {
    Contact: { route: (): Observable<string> => of('/contacts/:id') },
    Company: { route: (): Observable<string> => of('/companies/:id') },
    Property: { route: (): Observable<string> => of('/properties/:id') },
    Project: {
      deferred: true,
      route: (key?: number): Observable<string> => {
        return this.projectService.getProjectByIdBase(String(key)).pipe(
          untilComponentDestroyed(this),
          switchMap((project: Project) => of(`/projects/${project.pipeline_id}/${key}`)),
        );
      },
    },
  };
  newTable: boolean = false;

  get tagsCounter(): number {
    return this.queryFilters?.tags?.length || 0;
  }

  get filterCounter(): number {
    return this.filterValueList?.value?.length || 0;
  }

  get canCreateEngagement(): boolean {
    return this.store.selectSnapshot(UserState.hasPermission(NewPermission.ENGAGEMENTS_CREATE));
  }

  readonly nestedFiltersEnabled = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));
  readonly newPermissionsEnabled = this.store.selectSnapshot(
    AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
  );
  readonly companiesCount$: Observable<number> = iif(
    () => this.nestedFiltersEnabled,
    this.store.select(CompanyState.selectCompaniesCount),
    this._companiesCount,
  );
  readonly onPaginationChange = new Subject<TsPaginatorChangeEvent>();
  readonly searchInputControl: FormControl = new FormControl('');
  readonly pagination$: Observable<GraphqlResultPageInfo> = inject(Store).select(CompanyState.selectPagination);
  readonly companies$: Observable<Company[]> = inject(Store).select(CompanyState.selectCompanies);
  viewManagementOptions: Signal<TsMenuItem[]> = computed(() => {
    if (this.savedView()) {
      return this.savedViewUtilsService.getViewManagementMenuOptions(
        this.savedView,
        this.relatedToTypeCompany,
        this.isLoading,
      );
    }
  });

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _companiesService: CompaniesService,
    private readonly _router: Router,
    private readonly _accountService: AccountService,
    private readonly _userService: UserService,
    private readonly _matDialog: MatDialog,
    private readonly _snackBar: TsSnackbarService,
    private readonly store: Store,
    private readonly layoutService: LayoutService,
    private readonly actions: Actions,
    private readonly tsFilterUtilsService: TsFilterUtilsService,
    public readonly lookupService: LookupService,
    private readonly savedViewsSidebarService: SavedViewsSidebarService,
    private readonly savedViewUtilsService: SavedViewUtilsService,
    private readonly projectService: ProjectsService,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
    this._companiesService.accountId = this.account?.id;
    this.pagination = {
      length: 1,
      size: 1,
      page: 1,
      lastPage: 1,
      startIndex: 1,
      endIndex: 1 - 1,
    };

    this.relatedToRedirectConfig = this.store
      .selectSnapshot(ObjectsManagerState.get)
      .filter((x) => x.id)
      .reduce((p, c) => {
        return { ...p, [c.name]: { route: (): Observable<string> => of(`/objects/${c.id}/:id/details`) } };
      }, this.relatedToRedirectConfig);
  }

  ngOnInit(): void {
    if (this.nestedFiltersEnabled) {
      this.setupActionHandlers();
    }

    this.fetchRouteData();
    this.fetchFieldDefs();
    this.setupFiltersAndSavedViews();
    this.fillPageSize();
    this.fetchUsers();
    this.fetchCompanies();
    this.fetchCompaniesPagination();

    this.searchInputControl.valueChanges
      .pipe(untilComponentDestroyed(this), TsRxjsOperators.memoizedDebounceTime(500))
      .subscribe((query) => {
        this.queryFilters = { ...this.queryFilters, q: query };
        this.searchCompanies(this.queryFilters, false, 1);
      });

    this.savedViewsSidebarConfig = this.savedViewsSidebarService.getSidenavConfig(
      this.relatedToTypeCompany,
      this.store.selectSnapshot(UserState.hasPermission(NewPermission.COMPANIES_PUBLIC_CUSTOM_VIEWS)),
    );

    this.checkTable();
    this.getColDefs();
  }

  ngAfterViewInit(): void {
    this.tablePaginationWatcher();
    this.closeSnackBarOnLeave();
  }

  ngOnDestroy(): void {
    this._snackBar.dismiss();
  }

  private fetchRouteData(): void {
    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => (this.routeData = data));
  }

  private fetchFieldDefs(): void {
    this.fieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Company, {
        allowedFieldsOnly: true,
        addNestedFields: this.nestedFiltersEnabled,
      }),
    );

    this.setFilterNestedFields();

    this.handleAddInitialCompanyTableColumns();
  }

  addCompanyEngagementModal(engagement_type: string, company): void {
    this._matDialog
      .open(AddEngagementDialogComponent, {
        data: {
          options: this.routeData.options,
          engagement_type: engagement_type,
          company,
        },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(noop);
  }

  private setFilterNestedFields(): void {
    const typeFields = this.tsFilterUtilsService.getFilterList(this.fieldDefs)?.map((item) => {
      if (item.meta?.record_type === RelatedToType.User) {
        item.meta.options = this.getUserOptions();
        item.type = 'select';
      }

      return { ...item };
    });

    this.typeFields.next(typeFields);
  }

  private getUserOptions(): { label: string; value: string }[] {
    return (
      this.store
        // @ts-ignore
        .selectSnapshot(FieldDefsState.selectDropdownOptionsBy('users'))
        .map((x) => ({ value: x.id, label: x.name }))
    );
  }

  private setupFiltersAndSavedViews(): void {
    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data) => {
      this.savedView.set(data.savedView);
      UrlUtils.saveToLocalStorage('companiesSavedViewId', this.savedView()?.id, false);
      this.queryFilters = this.getQueryFiltersState();
      this.sortState = this.getSortState();
      this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
      this.renderSavedView();
      this.searchCompanies(this.queryFilters, false, 1);
      this._changeDetectorRef.markForCheck();
    });
  }

  private handleAddInitialCompanyTableColumns(): void {
    this.filteredFieldDefs = cloneDeep(this.fieldDefs);
    this.companiesTableColumns = this.fieldDefs.map(({ name }) => name);
    this.displayColumns = this.companiesTableColumns
      .filter((column) => !['checkbox'].includes(column))
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort(this.sortNameColumnToFirst);
    this.updateCompaniesTableColumns(this.displayColumns);
  }

  private sortNameColumnToFirst(a: string, b: string): number {
    return a === CompanyStandardFieldsMap.name ? -1 : 1;
  }

  private fillPageSize(): void {
    let pageSize = UrlUtils.getFromLocalStorage('companiesPageSize');
    if (pageSize) this._pageSize = parseInt(pageSize);
  }

  private savePageSize(): void {
    UrlUtils.saveToLocalStorage('companiesPageSize', this._paginator.pageSize.toString());
  }

  private fetchUsers(): void {
    this._userService.user$.pipe(untilComponentDestroyed(this)).subscribe((user: User) => (this.user = user));
  }

  private fetchCompanies(): void {
    iif(() => this.nestedFiltersEnabled, this.companies$, this._companiesService.companies$).subscribe(
      (companies: Company[]) => {
        this.companiesData = companies;
        this._changeDetectorRef.markForCheck();
      },
    );
  }

  private fetchCompaniesPagination(): void {
    this._companiesService.pagination$
      .pipe(untilComponentDestroyed(this))
      .subscribe((pagination: CompanyPagination) => {
        if (pagination) {
          this.pagination = pagination;
          this._companiesCount.next(pagination.length);
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  private tablePaginationWatcher(): void {
    this.onPaginationChange.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.savePageSize();
      this.searchCompanies(this.queryFilters, false, null, true);
    });
  }

  private closeSnackBarOnLeave(): void {
    this._router.events
      .pipe(
        untilComponentDestroyed(this),
        filter((route) => route instanceof NavigationStart),
      )
      .subscribe(() => this._snackBar.dismiss());
  }

  private updateCompaniesTableColumns(editableColumns: string[]): void {
    this.companiesTableColumns = ['checkbox', ...editableColumns];

    if (this.canCreateEngagement) {
      this.companiesTableColumns.push('action');
    }

    this.getColDefs();
  }

  private renderSavedView(): void {
    this.displayColumns = this.getDisplayColumnsState();
    this.displayColumns = this.displayColumns
      .filter((column) => this.fieldDefs.find((field) => field.name === column))
      .filter((column) => !['checkbox'].includes(column))
      .sort(this.sortNameColumnToFirst);

    this.updateCompaniesTableColumns(this.displayColumns);
  }

  private saveTableSort(): void {
    this.savedViewUtilsService.saveTableSort(this.savedView, this.sortState);
  }

  private saveColumns(): void {
    if (!this.savedView()) {
      this.savedView.set({
        id: null,
        name: 'Companies',
        related_to_type: RelatedToType.Company,
        definition: JSON.stringify({ table: { fields: this.displayColumns } }),
      });
      this._accountService.createSavedView(this.savedView()).pipe(untilComponentDestroyed(this)).subscribe(noop);
    } else {
      this.savedView.update((sv) => {
        return {
          ...sv,
          definition: JSON.stringify({
            table: { fields: this.displayColumns, sort: JSON.parse(sv.definition).table?.sort },
          }),
        };
      });

      this._accountService.updateSavedView(this.savedView()).pipe(untilComponentDestroyed(this)).subscribe(noop);
    }
  }

  private addDisplayColumn(fieldName: string): void {
    if (!this.displayColumns.includes(fieldName)) {
      this.displayColumns = this.generateDisplayColumnPreservingOrder(this.displayColumns, fieldName);
      this.updateCompaniesTableColumns(this.displayColumns);

      if (this.nestedFiltersEnabled) {
        this.checkFieldNameOnLoadedCompanies(fieldName);
      }

      this._changeDetectorRef.markForCheck();
    }
  }

  private removeDisplayColumn(fieldName: string): void {
    this.displayColumns.splice(
      this.displayColumns.findIndex((column) => column === fieldName),
      1,
    );
    this.companiesTableColumns.splice(
      this.companiesTableColumns.findIndex((column) => column === fieldName),
      1,
    );
    this._changeDetectorRef.markForCheck();
    this.getColDefs();
  }

  /**
   * When a new column is added we need to check if we have the data available in our store,
   * otherwise we need to fetch it.
   * @param fieldName
   * @private
   */
  private checkFieldNameOnLoadedCompanies(fieldName: string): void {
    // we are assuming that if at least one company has the field, all companies have it.
    if (!this.store.selectSnapshot(CompanyState.selectCompanies).some((company) => fieldName in company)) {
      this.searchCompanies(this.queryFilters);
    }
  }

  /**
   * SC-49680: Update the account definitions by deleting the outdated 'company' definition used for the add form.
   * Update the saved view definitions to include the new fields from the add form.
   * @param definitions - The new definitions to update the account with
   * @param savedView - The saved view to update the definition of
   */
  private updateCustomFormDefinition(definitions: Pick<Account, 'definitions'>, savedView: SavedView): void {
    this.account.definitions = definitions;

    this._accountService
      .updateSavedView(savedView)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => {
          // SC-49680: Remove the company definition from the account definitions if it exists
          if (this.account.definitions?.company) {
            delete this.account.definitions.company;
            return this.store.dispatch(
              new AccountActions.UpdateAccount({
                id: this.account.id,
                configuration: {
                  definitions: JSON.stringify(this.account.definitions),
                },
              }),
            );
          }
          return of(null);
        }),
      )
      .subscribe(() => {
        this.account = this.store.selectSnapshot(AccountState.getAccount);
        this._changeDetectorRef.detectChanges();
      });
  }

  private processRequestParams(pageIndex?: number): GraphqlParams {
    const sortFields = [
      {
        field: this.sortState.active,
        order: this.sortState.direction,
      },
    ];

    return {
      connection: GraphqlConnection.companies,
      fields: [...this.displayColumns, 'task_list_id'],
      filter: {
        fjson: this.queryFilters.fjson,
        pagination: {
          page: pageIndex || this._paginator?.activePage || 1,
          size: this._paginator?.pageSize ? this._paginator.pageSize : this._pageSize,
        },
        sort: sortFields,
        query: this.queryFilters.q,
        filterDictionary: {
          tags: this.queryFilters.tags?.join(',') ?? '',
          ...(+this._project?.id ? { project_id: +this._project?.id } : {}),
        },
      },
    };
  }

  private generateDisplayColumnPreservingOrder(displayColumns: string[], fieldName: string): string[] {
    if (displayColumns[0] === CompanyStandardFieldsMap.name) {
      return [displayColumns[0], fieldName, ...displayColumns.slice(1)];
    } else {
      return [fieldName, ...displayColumns];
    }
  }

  private setupActionHandlers(): void {
    this.actions.pipe(ofActionDispatched(CompanyActions.GetCompanies), untilComponentDestroyed(this)).subscribe(() => {
      this.isLoading.set(true);
    });

    this.actions.pipe(ofActionCompleted(CompanyActions.GetCompanies), untilComponentDestroyed(this)).subscribe(() => {
      this.isLoading.set(false);
      this._changeDetectorRef.detectChanges();
    });

    this.actions
      .pipe(ofActionDispatched(CompanyActions.ExportCompanies), untilComponentDestroyed(this))
      .subscribe(() => {
        this.exportingCsv = true;
      });

    this.actions
      .pipe(ofActionCompleted(CompanyActions.ExportCompanies), untilComponentDestroyed(this))
      .subscribe(() => {
        this.exportingCsv = false;
        this._changeDetectorRef.detectChanges();
      });
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  private captureOriginalStates(): void {
    this._originalStates = {
      displayColumns: this.getDisplayColumnsState(),
      sort: this.getSortState(),
      queryFilters: { ...this.getQueryFiltersState() },
      filterValueList: this.getFilterValueListState(),
    };
  }

  private getSortState(): TableSortState {
    const savedSort = JSON.parse(this.savedView()?.definition)?.table?.sort;

    return {
      active: savedSort?.active || 'name',
      direction: savedSort?.direction || 'asc',
    };
  }

  private setSortState(value: TableSortState): void {
    this._originalStates.sort = {
      active: value.active,
      direction: value.direction,
    };
  }

  private getDisplayColumnsState(): string[] {
    return removeArchivedFieldsFromTableDefinition(
      this.savedView()?.definition ? JSON.parse(this.savedView().definition).table.fields : this._defaultColumns,
      this.fieldDefs,
    );
  }

  private setDisplayColumnsState(value: string[]) {
    this._originalStates.displayColumns = value;
  }

  private getFilterValueListState(): FilterListItemValueModel[] {
    return this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]');
  }

  private setFilterValueListState(value: FilterListItemValueModel[]) {
    this._originalStates.filterValueList = value;
  }

  private getQueryFiltersState(): Partial<QueryConfig.QueryFilters> {
    return JSON.parse(this.savedView().saved_filter?.filters || '{}');
  }

  private setQueryFiltersState(value: Partial<QueryConfig.QueryFilters>) {
    this._originalStates.queryFilters = value;
  }

  private openTrackStateSnackbar(changeType: ChangeType): void {
    this._changeTriggers.add(changeType);
    this.captureOriginalStates();

    if (this.store.selectSnapshot(UserState.hasPermission(NewPermission.COMPANIES_PUBLIC_CUSTOM_VIEWS))) {
      this._snackBar.openApplyChanges(this.resetToOriginalStates.bind(this), this.saveAllChanges.bind(this));
    } else {
      this._snackBar.openApplyChanges(this.resetToOriginalStates.bind(this));
    }
  }

  private clearChangeTriggers(): void {
    this._changeTriggers.clear();
  }

  private resetToOriginalStates(): void {
    if (this._changeTriggers.has('columns')) {
      this.displayColumns = this.getDisplayColumnsState();
      this.updateCompaniesTableColumns(this.displayColumns);
    }

    if (this._changeTriggers.has('sort')) {
      this.sortState = this.getSortState();
      this.searchCompanies(this.queryFilters);
    }

    if (this._changeTriggers.has('tags') || this._changeTriggers.has('filters')) {
      if (this._changeTriggers.has('filters')) {
        this.queryFilters = this.getQueryFiltersState();
        this.filterValueList.next(this.getFilterValueListState());
        this.onCloseByBackdropClick();
      }

      if (this._changeTriggers.has('tags')) {
        this.queryFilters = {
          ...this._originalStates.queryFilters,
          tags: this.getQueryFiltersState().tags,
        };
      }

      this.searchCompanies(this.queryFilters);
    }

    this.clearChangeTriggers();
    this._changeDetectorRef.markForCheck();
  }

  private saveAllChanges(): void {
    if (this._changeTriggers.has('columns')) {
      this.setDisplayColumnsState(this.displayColumns);
      this.saveColumns();
    }

    if (this._changeTriggers.has('sort')) {
      this.setSortState(this.sortState);
      this.saveTableSort();
    }

    if (this._changeTriggers.has('tags') || this._changeTriggers.has('filters')) {
      if (this._changeTriggers.has('filters')) {
        this.setFilterValueListState(this.filterValueList.value);
      }

      if (this._changeTriggers.has('tags')) {
        this.setQueryFiltersState(this.queryFilters);
      }

      this.saveFilter(this.queryFilters);
    }

    this.clearChangeTriggers();
  }

  private saveFilter(filters: Partial<QueryConfig.QueryFilters>): void {
    this.savedViewUtilsService.saveFilter(filters, this.savedView);
  }

  public isCompanySelected(company: Company): boolean {
    return this.selection.isSelected(Number(company.id));
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.companiesData.length;
    return numSelected === numRows;
  }

  public toggleCompany(company: Company): void {
    this.selection.toggle(Number(company.id));
  }

  public masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.companiesData.forEach(({ id }: Company) => this.selection.select(Number(id)));
  }

  public lookupField(name: string): FieldDef | undefined {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  public openMassAddTagDialog(): void {
    this._matDialog
      .open(MassUpdateTagsDialog, {
        maxWidth: '50vw',
        width: '100%',
        maxHeight: '80vh',
        height: 'auto',
        data: {
          account_id: this.account.id,
          related_to_type: RelatedToType.Company,
          selected_items_id: this.selection.selected,
        },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((result) => !!result),
      )
      .subscribe(() => {
        this.selection.clear();
        this._router.navigate(['/companies']);
      });
  }

  public goToCompany(id: string): void {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this._router.navigate(['/companies/', id]);
    this._changeDetectorRef.markForCheck();
  }

  public onTagsUpdated(tags: Array<string>): void {
    if (!this.queryFilters) {
      this.queryFilters = { tags: [] };
    }

    if (!this.queryFilters.fjson) {
      this.queryFilters['fjson'] = '[]';
    }

    this.openTrackStateSnackbar('tags');

    this.queryFilters.tags = tags;

    this.searchCompanies(this.queryFilters);
  }

  public exportCompanies(): void {
    if (this.nestedFiltersEnabled) {
      const params = this.processRequestParams();
      this.store.dispatch(new CompanyActions.ExportCompanies(params, this.savedView()?.id.toString()));
    } else {
      this.exportingCsv = true;
      this.searchCompanies(this.queryFilters, true);
    }
  }

  public onSelectionChange($event: MatButtonToggleChange): Promise<boolean> {
    return this._router.navigate([`/${$event.value}/`]);
  }

  public openAddCompanyDrawerForm(): void {
    this.layoutService.openComponentDrawer<CustomFormOptions>(CustomFormComponent, {
      inputs: {
        relatedToType: RelatedToType.Company,
        renderAttachFileField: false,
        allowedFields: this.fieldDefs.filter((field) => !this._fieldsToRemove.includes(field.name)),
        selectedFields: JSON.parse(this.savedView().definition)?.add_form_definition?.fields
          ? JSON.parse(this.savedView().definition).add_form_definition.fields
          : [
              { name: 'name', isRequired: true, isRemovable: false },
              { name: 'website', isRequired: false, isRemovable: true },
              { name: 'tag_list', isRequired: false, isRemovable: true },
              { name: 'address_string', isRequired: false, isRemovable: true },
            ],
        dataSources: {},
        commonData: {
          formFieldData: this.routeData,
          disableItemSuggestion: false,
          existingCompanies: this.companies$,
        },
        saveAction: this.saveAction.bind(this),
        enableEditing: this.store.selectSnapshot(UserState.hasPermission(NewPermission.COMPANIES_MANAGE_ADD_FORM)),
      },
      outputs: {
        onTemplateChanged: (fields: CustomField[]) => {
          // SC-49680: Update the saved view definition to include the new fields
          this.savedView.update((sv) => {
            return {
              ...sv,
              definition: JSON.stringify({
                ...JSON.parse(sv.definition),
                add_form_definition: {
                  fields,
                },
              }),
            };
          });

          this.updateCustomFormDefinition(this.account.definitions, this.savedView());
        },
        onNavigationByUrlRequested: (url: string) => this._router.navigateByUrl(url),
      },
    });
  }

  public saveAction(data) {
    this._companiesService
      .createCompany(data as Company)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (newCompany) => {
          this._snackBar.open('Company successfully created', 'success');
          this._router.navigateByUrl(`/companies/${newCompany.id}`);
        },
        error: (error) => {
          let errorMessage = 'Unable to create a new company at this time. Please try again later.';

          if (error?.error?.error != undefined) {
            errorMessage = error?.error?.error;
          }

          this._snackBar.open(errorMessage, 'error', null, { duration: 5000 });
        },
        complete: () => {
          this.layoutService.closeComponentDrawer();
          this._changeDetectorRef.detectChanges();
        },
      });
  }

  public trackByFn(index: number, item: any): any {
    return item?.id || index;
  }

  public dropColumns(event: CdkDragDrop<string[]>): void {
    this.openTrackStateSnackbar('columns');

    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.displayColumns, event.previousIndex, event.currentIndex);
      this.updateCompaniesTableColumns(this.displayColumns);
    }
  }

  onColumnMoved(event: any): void {
    TsTableService.moveColumns(event, this.displayColumns, ['openDetails', 'checkbox'], (columns: string[]) => {
      this.updateCompaniesTableColumns(columns);
      this.openTrackStateSnackbar('columns');
    });
  }

  public filterFields(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredFieldDefs = this.fieldDefs.filter((fieldDef) => fieldDef.label.toLowerCase().includes(value));
  }

  public filterFieldsInputKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;

    const fieldDef = this.filteredFieldDefs[0];
    const isFieldVisible = this.fieldDefs.find((item) => item.name === fieldDef.name);

    if (isFieldVisible) this.removeDisplayColumn(fieldDef.name);
    else this.addDisplayColumn(fieldDef.name);
  }

  public toggleDisplayColumn(fieldName: string, change: MatCheckboxChange): void {
    this.openTrackStateSnackbar('columns');

    if (change.checked) {
      this.addDisplayColumn(fieldName);
    } else {
      this.removeDisplayColumn(fieldName);
    }
  }

  public searchCompanies(
    event: Partial<QueryConfig.QueryFilters>,
    exportCsv = false,
    pageIndex?: number,
    preserveFilter = false,
  ): void {
    if (!preserveFilter) {
      this.queryFilters = { ...event };
    }

    if (this.nestedFiltersEnabled) {
      const params = this.processRequestParams(pageIndex);

      this.store.dispatch(new CompanyActions.GetCompanies(params));
    } else {
      this.isLoading.set(true);
      this.queryFilters = { ...event };

      this._companiesService
        .search(
          {
            fjson: this.queryFilters.fjson,
            sort: this.sortState.active,
            tags: this.queryFilters.tags?.join(','),
            order: this.sortState.direction,
            q: this.queryFilters.q,
            project_id: +this._project?.id,
          },
          pageIndex || this._paginator?.activePage || 1,
          this._paginator?.pageSize ? this._paginator.pageSize : this._pageSize,
          exportCsv,
          this.savedView().id,
        )
        .pipe(untilComponentDestroyed(this))
        .subscribe({
          next: (res) => {
            if (exportCsv && res.data) {
              if (res.data?.message) {
                this._snackBar.open(res.data?.message, 'info');
              } else {
                generateDownloadableFile(res.data, FileFormat.CSV, `Companies`);
              }
              this.exportingCsv = false;
            }
          },
          complete: () => {
            this.isLoading.set(false);
            this._changeDetectorRef.detectChanges();
          },
          error: () => {
            this.isLoading.set(false);
            this._changeDetectorRef.detectChanges();
          },
        });
    }

    this.getColDefs();
  }

  public resetColumns(): void {
    this.openTrackStateSnackbar('columns');
    this.displayColumns = [...this._defaultColumns];
    this.updateCompaniesTableColumns(this.displayColumns);
    this.sortState = {
      active: 'name',
      direction: 'desc',
    };
  }

  public disableUndo(): boolean {
    if (this.displayColumns.length !== this._defaultColumns.length) {
      return false;
    }

    return this.displayColumns.every((item) => this._defaultColumns.includes(item));
  }

  public onCloseByBackdropClick(): void {
    this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
  }

  public onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  public onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  public onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  public onFilterCleared(): void {
    this.openTrackStateSnackbar('filters');
    this.filterValueList.next([]);
    this.queryFilters = { fjson: `[]`, q: '', tags: this.queryFilters.tags };
    this.searchCompanies(this.queryFilters, false, 1);
  }

  public onFilterApplied(): void {
    this.openTrackStateSnackbar('filters');
    this.queryFilters = {
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
      q: '',
      tags: this.queryFilters.tags,
    };

    this.searchCompanies(this.queryFilters, false, 1);

    this._filtersOverlayBox.closeOverlay();
  }

  private getColDefs(): void {
    if (this.newTable) {
      this.columnDefs = this.displayColumns.map((column) => this.lookupField(column));
      this.setColumnsAttributes();
    }
  }

  private setColumnsAttributes() {
    this.columnDefs.map((fieldDef, index) => {
      if (fieldDef) {
        if (fieldDef.meta?.record_type === RelatedToType.User) {
          fieldDef.meta.options = this.getUserOptions();
        }

        fieldDef.tsTableOptions = {
          resizable: true,
          sort: this.sortState.active === fieldDef.name ? this.sortState.direction : null,
          cellEditor: fieldDef.type === 'tags' ? TsTableTagsEditor : null,
          cellStyle: nonEditableStyle(fieldDef),
          ...fieldDef.tsTableOptions,
        };
        this.setTableActions();
      }
    });
  }

  setTableActions(): void {
    this.tsActions = [
      {
        label: 'Log Call',
        action: (item) => this.addCompanyEngagementModal('Call', item),
        icon: 'space_dashboard',
      },
      {
        label: 'Log Meeting',
        action: (item) => this.addCompanyEngagementModal('Meeting', item),
        icon: 'edit',
      },
      {
        label: 'Log Social Media',
        action: (item) => this.addCompanyEngagementModal('Social Media', item),
        icon: 'delete',
      },
    ];
  }

  onCellEditingStopped(event) {
    const editedData = event.data;
    this.updateCompany(editedData);
  }

  updateCompany(company: Company): void {
    this._companiesService.updateCompany(company).subscribe();
  }

  onSelectionChanged(event) {
    if (!event.length) this.selection.clear();
    if (event.length > 1) {
      this.masterToggle();
    } else if (event.length === 1) {
      this.toggleCompany(event[0]);
    }
  }

  public sortChanged(event: MatSort): void {
    this._sort = event;
    this.sortState = {
      active: this._sort.active,
      direction: this._sort.direction as 'asc' | 'desc',
    };
    this._paginator.activePage = 1;
    this.searchCompanies(this.queryFilters, false, null, true);
    this.openTrackStateSnackbar('sort');
  }

  onSortChanged(event: SortChangedEvent): void {
    if (event.columns && event.columns.length > 0) {
      const sortedColumn = event.columns.filter((col) => col.getSort());
      const columnDef = this.columnDefs.find((def) => def.name === sortedColumn[0]?.getColDef().field);
      this.sortState = {
        active: columnDef?.meta?.sorting || sortedColumn[0]?.getColDef().field || 'name',
        direction: sortedColumn[0]?.getSort() || 'asc',
      };
    } else {
      this.sortState = {
        active: 'name',
        direction: 'asc',
      };
    }
    this._paginator.activePage = 1;
    this.searchCompanies(this.queryFilters, false, null, true);
    this.openTrackStateSnackbar('sort');
  }

  private checkTable(): void {
    this.newTable = this.account.features.includes(FeatureFlag.NEW_TABLE);
  }
}
